import { CheckCircle2 } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { LayoutAcesso, mensagemDeErro } from "@/components/acesso/LayoutAcesso";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { Usuario } from "@/types";

/** Destino do link de confirmação da troca de e-mail. Funciona logado ou não. */
export function ConfirmarEmailPage() {
  const [parametros] = useSearchParams();
  const token = parametros.get("token") ?? "";
  const { usuario, atualizarUsuario } = useAuth();
  const navegar = useNavigate();
  const [estado, setEstado] = React.useState<
    { tipo: "carregando" } | { tipo: "ok"; email: string } | { tipo: "erro"; mensagem: string }
  >({ tipo: "carregando" });
  // O link é de uso único: o StrictMode monta o efeito duas vezes em dev, e a
  // segunda chamada receberia "link já usado". Um ref garante uma chamada só.
  const chamou = React.useRef(false);

  React.useEffect(() => {
    if (chamou.current) return;
    chamou.current = true;
    if (!token) {
      setEstado({ tipo: "erro", mensagem: "O link está incompleto. Abra-o de novo a partir do e-mail." });
      return;
    }
    api<{ usuario: Usuario }>("/auth/email/confirmar", { method: "POST", body: { token } })
      .then(({ usuario: atualizado }) => setEstado({ tipo: "ok", email: atualizado.email }))
      .catch((falha) =>
        setEstado({ tipo: "erro", mensagem: mensagemDeErro(falha, "Não foi possível confirmar.") }),
      );
  }, [token]);

  // Se quem confirmou é quem está logado, o cabeçalho passa a mostrar o novo e-mail.
  React.useEffect(() => {
    if (estado.tipo === "ok" && usuario && usuario.email !== estado.email) {
      api<Usuario>("/auth/eu")
        .then(atualizarUsuario)
        .catch(() => undefined);
    }
  }, [estado, usuario, atualizarUsuario]);

  const botao = (
    <Button className="mt-8 w-full" onClick={() => navegar("/", { replace: true })}>
      {usuario ? "Voltar ao OLÉ" : "Ir para a tela de entrada"}
    </Button>
  );

  if (estado.tipo === "carregando") {
    return (
      <LayoutAcesso titulo="Confirmando e-mail" subtitulo="Só um instante…">
        <Skeleton className="mt-8 h-12 w-full" />
      </LayoutAcesso>
    );
  }

  if (estado.tipo === "erro") {
    return (
      <LayoutAcesso titulo="Link indisponível" subtitulo={estado.mensagem}>
        <p className="mt-6 text-sm text-muted-foreground">
          Peça a troca de novo em “Minha conta”, dentro do OLÉ.
        </p>
        {botao}
      </LayoutAcesso>
    );
  }

  return (
    <LayoutAcesso
      titulo="E-mail confirmado"
      subtitulo={
        <>
          A partir de agora, entre com <b className="text-fs-escuro">{estado.email}</b>.
        </>
      }
    >
      <CheckCircle2 className="mt-8 size-10 text-primary" aria-hidden />
      {botao}
    </LayoutAcesso>
  );
}
